// Image manager for the product editor. Lists the product's images as a grid of
// thumbnails (the first is the gallery's "Main" image), with controls to upload,
// reorder, set-as-main, and delete.
//
// Two modes, transparent to the controls below:
//   - Existing product (has an id): edits persist immediately against its row.
//   - New product (no id yet): images are staged locally as File objects with
//     object-URL previews, then uploaded when the product is saved. This avoids
//     the old "save the product first" gate.
import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box, Typography, Button, IconButton, Chip, Alert, Tooltip, CircularProgress,
} from '@mui/material';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutlined';
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import {
  loadProductImages, addProductImages, removeProductImage, reorderProductImages,
  addPendingImages, removePendingImage, reorderPendingImages,
} from '../../../store/product-editor-slice.js';

const MAX_IMAGES = 8;
const ACCEPT = 'image/jpeg,image/png,image/webp';

const ProductImagesSection = () => {
  const dispatch = useDispatch();
  const fileInputRef = useRef(null);
  const {
    productId, images, pendingImages, imagesBusy, imagesError,
  } = useSelector((state) => state.productEditor);

  // No saved row yet → operate on the locally-staged list.
  const isPending = !productId;
  const list = isPending ? pendingImages : images;

  useEffect(() => {
    if (productId) dispatch(loadProductImages(productId));
  }, [dispatch, productId]);

  const atLimit = list.length >= MAX_IMAGES;

  const idOf = (img) => (isPending ? img.tempId : img.id);
  const srcOf = (img) => (isPending ? img.previewUrl : `/api/catalog/images/${img.id}`);
  const orderIds = () => list.map(idOf);

  const applyOrder = (ids) => {
    if (isPending) dispatch(reorderPendingImages(ids));
    else dispatch(reorderProductImages(productId, ids));
  };

  const handleFiles = (event) => {
    const input = event.target;
    const files = Array.from(input.files || []);
    if (files.length > 0) {
      if (isPending) dispatch(addPendingImages(files));
      else dispatch(addProductImages(productId, files));
    }
    input.value = '';
  };

  const handleRemove = (img) => {
    if (isPending) dispatch(removePendingImage(img.tempId));
    else dispatch(removeProductImage(productId, img.id));
  };

  const move = (index, delta) => {
    const ids = orderIds();
    const target = index + delta;
    if (target < 0 || target >= ids.length) return;
    [ids[index], ids[target]] = [ids[target], ids[index]];
    applyOrder(ids);
  };

  const makeMain = (index) => {
    if (index === 0) return;
    const ids = orderIds();
    const [moved] = ids.splice(index, 1);
    ids.unshift(moved);
    applyOrder(ids);
  };

  return (
    <Box>
      <Box sx={{
        display: 'flex', alignItems: 'center', gap: 1, mb: 1,
      }}
      >
        <Typography component="span" variant="subtitle2">Images</Typography>
        <Typography variant="caption" color="text.secondary">
          {`${list.length} / ${MAX_IMAGES}`}
        </Typography>
        {imagesBusy && <CircularProgress size={16} />}
      </Box>

      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
        The first image is shown as the main image; the rest appear as clickable thumbnails.
        {isPending && ' New images are uploaded when you save the product.'}
      </Typography>

      {imagesError && <Alert severity="error" sx={{ mb: 1.5 }}>{imagesError}</Alert>}

      {list.length > 0 && (
        <Box sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))',
          gap: 1.5,
          mb: 1.5,
        }}
        >
          {list.map((img, index) => (
            <Box
              key={idOf(img)}
              sx={{
                border: 1,
                borderColor: index === 0 ? 'primary.main' : 'divider',
                borderRadius: 1,
                overflow: 'hidden',
                position: 'relative',
              }}
            >
              <Box
                component="img"
                src={srcOf(img)}
                alt={img.alt_text || ''}
                sx={{
                  display: 'block', width: '100%', height: 90, objectFit: 'cover', bgcolor: 'action.hover',
                }}
              />
              {index === 0 && (
                <Chip
                  icon={<StarIcon sx={{ fontSize: 14 }} />}
                  label="Main"
                  size="small"
                  color="primary"
                  sx={{
                    position: 'absolute', top: 4, left: 4, height: 20,
                  }}
                />
              )}
              <Box sx={{
                display: 'flex', justifyContent: 'center', gap: 0.25, p: 0.25, borderTop: 1, borderColor: 'divider',
              }}
              >
                <Tooltip title="Set as main">
                  <span>
                    <IconButton size="small" disabled={imagesBusy || index === 0} onClick={() => makeMain(index)}>
                      {index === 0 ? <StarIcon fontSize="small" /> : <StarBorderIcon fontSize="small" />}
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title="Move left">
                  <span>
                    <IconButton size="small" disabled={imagesBusy || index === 0} onClick={() => move(index, -1)}>
                      <ArrowBackIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title="Move right">
                  <span>
                    <IconButton size="small" disabled={imagesBusy || index === list.length - 1} onClick={() => move(index, 1)}>
                      <ArrowForwardIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title="Remove image">
                  <span>
                    <IconButton size="small" color="error" disabled={imagesBusy} onClick={() => handleRemove(img)}>
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              </Box>
            </Box>
          ))}
        </Box>
      )}

      <input
        ref={fileInputRef}
        type="file"
        accept={ACCEPT}
        multiple
        onChange={handleFiles}
        style={{ display: 'none' }}
      />
      <Button
        variant="outlined"
        size="small"
        startIcon={<AddPhotoAlternateIcon />}
        disabled={imagesBusy || atLimit}
        onClick={() => fileInputRef.current?.click()}
      >
        Add images
      </Button>
      {atLimit && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
          {`Maximum of ${MAX_IMAGES} images reached.`}
        </Typography>
      )}
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
        JPEG, PNG, or WebP · up to 5 MB each.
      </Typography>
    </Box>
  );
};

export default ProductImagesSection;
